import { useCallback, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { sendTestsToUsers as sendTestToUserService } from 'views/screens/Platform-administrator/services'
import { useNotification } from 'lib/context/notification.context'
import { cleanProcess } from 'ducks/createProcess'
import { useTranslation } from 'react-i18next'
/* eslint-disable */

interface DataToSend {
  process_id: number
  users_data: { user_id: number; language?: string }[]
  company_id: number
  type: string
  count?: number
  email_template: {
    textOne?: string
    textTwo?: string
    textThree?: string
    textFour?: string
    textThreeOptions?: string[]
    date?: string
    logo?: string
  }
}

interface WaitingResult {
  status: 'waiting' | 'success' | 'error'
  message?: string
}

interface SuccessResult {
  status: 'OK'
  response: any
}

interface ErrorResult {
  status: 'error'
  message: string
}

interface SendUserResult {
  send_mail?: boolean
  delivery_status?: string
}

interface SendResponse {
  status: string
  users?: SendUserResult[] | string
  message?: string
  alternative_inventory?: { name: string }
}

interface SendBatchProgress {
  completed: number
  sent: number
  failed: number
  uncertain: number
  alternativeInventory: string | null
}

export type Result = WaitingResult | SuccessResult | ErrorResult

interface SendToUsersResult {
  handleSendExamToUsers: (data: DataToSend, totalCalls?: number, completedCalls?: number) => void
  loadingSendExamToUser: boolean
  resultSendExamToUser: Result
  setLoading: (loading: boolean) => void
}

export const useSendExamToUsers = (): SendToUsersResult => {
  const { t } = useTranslation()
  const { getError, getSuccess, getWarning } = useNotification()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [loadingSendExamToUser, setLoading] = useState(false)
  const [resultSendExamToUser, setResult] = useState<Result>({ status: 'waiting', message: '' })
  const batch = useRef<SendBatchProgress>({
    completed: 0,
    sent: 0,
    failed: 0,
    uncertain: 0,
    alternativeInventory: null,
  })

  const handleSendExamToUsers = useCallback(
    (data: DataToSend, totalCalls?: number, completedCalls?: number) => {
      if (completedCalls === 1 || !totalCalls) {
        batch.current = {
          completed: 0,
          sent: 0,
          failed: 0,
          uncertain: 0,
          alternativeInventory: null,
        }
        setResult({ status: 'waiting', message: '' })
      }
      setLoading(true)

      const finishCall = () => {
        batch.current.completed += 1
        if (batch.current.completed < (totalCalls || 1)) return

        setLoading(false)
        if (batch.current.failed > 0) {
          const message =
            batch.current.uncertain > 0
              ? 'Hay envíos pendientes de verificación. No reintentes ni devuelvas sus créditos hasta revisarlos.'
              : `No se pudo confirmar el envío a ${batch.current.failed} personas.`
          setResult({ status: 'error', message })
          if (batch.current.uncertain > 0) getWarning(message)
          else getError(message)
          return
        }
        if (batch.current.sent === 0) {
          setResult({ status: 'error', message: t('global.notifications.testAlreadySent') })
          getWarning(t('global.notifications.testAlreadySent'))
          return
        }

        setResult({ status: 'success' })
        dispatch(cleanProcess())
        if (batch.current.alternativeInventory) {
          getWarning(`Los créditos se descontaron del inventario ${batch.current.alternativeInventory}.`)
        }
        getSuccess('Se enviaron las pruebas a los usuarios')
        navigate('/')
      }

      sendTestToUserService(data)
        .then((response: SendResponse) => {
          if (response.status !== 'OK') {
            batch.current.failed += data.users_data.length
          } else if (Array.isArray(response.users)) {
            batch.current.failed += Math.max(0, data.users_data.length - response.users.length)
            for (const user of response.users) {
              if (user.send_mail) batch.current.sent += 1
              else if (user.delivery_status !== 'already_sent') {
                batch.current.failed += 1
                if (user.delivery_status === 'pending' || user.delivery_status === 'review') {
                  batch.current.uncertain += 1
                }
              }
            }
            if (response.alternative_inventory) {
              batch.current.alternativeInventory = response.alternative_inventory.name
            }
          } else if (response.users !== 'The exam has already been sent to all selected users') {
            batch.current.failed += data.users_data.length
          }
          finishCall()
        })
        .catch((error) => {
          console.error('error al enviar las pruebas', error)
          batch.current.failed += data.users_data.length
          finishCall()
        })
    },
    [dispatch, getError, getSuccess, getWarning, navigate, t]
  )

  return { handleSendExamToUsers, loadingSendExamToUser, resultSendExamToUser, setLoading }
}
